import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { Search, CheckCircle2, Circle, AlertCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const REF_RE = /^TV-AFR-[A-Z0-9]{8}$/;

export default function TrackVerification() {
  const [sp, setSp] = useSearchParams();
  const [ref, setRef] = useState((sp.get("ref") || "").toUpperCase());
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (sp.get("ref")) lookup((sp.get("ref") || "").toUpperCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookup = async (r) => {
    const clean = (r || ref).trim().toUpperCase();
    if (!REF_RE.test(clean)) {
      setErr("Reference format looks off. Use the format TV-AFR-XXXXXXXX.");
      setData(null);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get(`/verification-requests/track/${clean}`);
      setData(data);
      setSp({ ref: clean });
    } catch (e) {
      const msg = e?.response?.data?.detail || "We could not find that reference. Check the number and try again, or contact THOM Verify Support.";
      setErr(msg);
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => { e.preventDefault(); lookup(ref); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />
      <section className="max-w-[1100px] mx-auto px-6 py-14 lg:py-20">
        <div className="max-w-[720px]">
          <div className="kicker text-white/60">Verification tracking</div>
          <h1 className="display text-4xl lg:text-6xl mt-3 leading-[1.02]">Track your verification request.</h1>
          <p className="mt-5 text-white/70 text-[15px] leading-[1.75]">
            Enter your THOM Verify reference number to view the current progress of your
            independent verification assignment.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-[720px]">
          <Input
            value={ref}
            onChange={(e) => setRef(e.target.value.toUpperCase())}
            placeholder="TV-AFR-XXXXXXXX"
            className="bg-white/5 border-white/20 text-white placeholder:text-white/40 h-12 font-mono tracking-widest"
            data-testid="track-ref-input"
            autoComplete="off"
            spellCheck="false"
          />
          <Button type="submit" disabled={busy || !ref} className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black h-12 px-6" data-testid="track-submit">
            <Search className="h-4 w-4 mr-2" /> {busy ? "Looking up…" : "Track Request"}
          </Button>
        </form>

        {err && (
          <div className="mt-6 max-w-[720px] border border-red-500/30 bg-red-500/[0.06] p-4 flex items-start gap-3" data-testid="track-error">
            <AlertCircle className="h-5 w-5 text-red-400 mt-0.5" />
            <div className="text-[13.5px] text-white/85">{err}</div>
          </div>
        )}

        {data && (
          <div className="mt-10" data-testid="track-result">
            {/* Header card */}
            <div className="border border-white/15 bg-white/[0.03] p-6 lg:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Reference</div>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <span className="text-2xl mono text-[#B08C4A] tracking-widest" data-testid="track-ref-display">{data.reference}</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(data.reference); toast.success("Reference copied"); }}
                      className="text-[11px] text-white/60 hover:text-white inline-flex items-center gap-1"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Overall progress</div>
                  <div className="mt-1 text-3xl font-semibold text-white" data-testid="track-progress">{data.progress_percent}%</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5 text-[12.5px]">
                <Meta label="Country"          value={data.country} />
                <Meta label="Category"         value={data.category || "—"} />
                <Meta label="Request date"     value={fmtDate(data.request_date)} />
                <Meta label="Last updated"     value={fmtDate(data.last_updated)} />
                <Meta label="Current stage"    value={data.current_stage_label} accent />
                <Meta label="Next action"      value={data.next_action} />
                <Meta label="Assigned team"    value={data.assigned_team} />
                <Meta label="Further info?"    value={data.needs_customer_info ? "Yes — check your email" : "None required"} accent={data.needs_customer_info} tone={data.needs_customer_info ? "amber" : undefined} />
              </div>

              <div className="mt-6 border-t border-white/10 pt-5">
                <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Status</div>
                <p className="mt-1.5 text-[14px] text-white/90 leading-[1.65]" data-testid="track-status-msg">{data.status_message}</p>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/verification-requests/${data.reference}/certificate.pdf`} target="_blank" rel="noreferrer" data-testid="track-download-cert">
                  <Button className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Download PDF Certificate</Button>
                </a>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => window.print()} data-testid="track-print">Print</Button>
                <a href="mailto:support@thomverify.africa" data-testid="track-contact-support">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white">Contact Support</Button>
                </a>
                <Link to="/africa/countries">
                  <Button variant="ghost" className="text-white/70 hover:text-white hover:bg-white/5">Back to countries</Button>
                </Link>
              </div>
            </div>

            {/* Pipeline tracker */}
            <div className="mt-6 border border-white/10 bg-white/[0.02] p-6" data-testid="track-pipeline">
              <div className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/60 mb-4">Ten-stage pipeline</div>
              <ol className="space-y-2.5">
                {(data.pipeline_stages || []).map((stage, i) => {
                  const label = data.pipeline_labels?.[stage] || stage;
                  const currentIdx = data.pipeline_stages.indexOf(data.current_stage);
                  const done = i < currentIdx;
                  const current = i === currentIdx;
                  return (
                    <li key={stage} data-testid={`track-stage-${stage}`} className={`flex items-start gap-3 p-3 border ${current ? "border-[#B08C4A]/50 bg-[#B08C4A]/10" : done ? "border-emerald-500/25 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
                      {done ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                      ) : current ? (
                        <span className="h-5 w-5 rounded-full bg-[#B08C4A] mt-0.5 shrink-0 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 rounded-full bg-black" />
                        </span>
                      ) : (
                        <Circle className="h-5 w-5 text-white/25 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="mono text-[10px] uppercase tracking-widest text-white/50">Stage {i + 1}</span>
                          {current && <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#B08C4A]/25 text-[#D4B278]">Current</span>}
                          {done && <span className="mono text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/20 text-emerald-300">Complete</span>}
                        </div>
                        <div className={`mt-1 text-[13.5px] ${current ? "text-[#D4B278] font-medium" : done ? "text-white/85" : "text-white/50"}`}>{label}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <p className="mt-6 text-[11.5px] text-white/40 max-w-[720px] leading-[1.6]">
              For confidentiality reasons, we do not display internal notes, evidence artefacts,
              investigator identities, pricing details or third-party contact information on this
              public status page.
            </p>
          </div>
        )}
      </section>
      <AfricaFooter />
    </div>
  );
}

const Meta = ({ label, value, accent, tone }) => (
  <div>
    <div className="mono text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
    <div className={`mt-1.5 ${accent ? (tone === "amber" ? "text-amber-300" : "text-[#D4B278]") : "text-white/90"}`}>{value}</div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};
